<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero - scattered calligraphic flourishes provide ambient
                 background texture (not a single foreground swoosh). Each
                 SVG is sized + positioned individually so the eye reads
                 them as soft hand-drawn marks, not a graphic element. The
                 right-shifted gear watermark anchors visual weight on the
                 right; the flourishes float around the headline. -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 40px;">
                <div class="mkt-hero-arcs" aria-hidden="true">
                    <!-- Two concentric-arc clusters anchored in opposite
                         corners (bottom-left + top-right). Each cluster is
                         12 parallel-offset Bezier curves sharing one
                         gradient stroke. Lines are thin (0.6px) and the
                         cluster opacity is low (0.55) so the pattern reads
                         as ambient signal-wave atmosphere. -->
                    <svg class="mkt-hero-arc mkt-hero-arc--bl" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradBL" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%"   stop-color="#4F46E5" stop-opacity="0" />
                                <stop offset="38%"  stop-color="#4F46E5" stop-opacity="1" />
                                <stop offset="100%" stop-color="#7C3AED" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradBL)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                    <svg class="mkt-hero-arc mkt-hero-arc--tr" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradTR" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%"   stop-color="#0EA5E9" stop-opacity="0" />
                                <stop offset="38%"  stop-color="#0EA5E9" stop-opacity="1" />
                                <stop offset="100%" stop-color="#4F46E5" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradTR)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                </div>
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="settings" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <h1 class="text-weight-bold q-mb-sm" style="font-size:clamp(2rem,4vw,3rem);line-height:1.15;color:#0F172A;">
                        Everything it takes to get<br>documents <span class="text-mkt-indigo">signed</span>
                        <span class="visually-hidden"> - eSign Launchpad features: send and sign, identity verification, payment collection, audit trail, and a developer API</span>
                    </h1>
                    <p style="font-size:1.1rem;max-width:580px;margin:0 auto;color:#475569;">
                        Send and sign, verify identity, collect payment, and prove it all with a tamper-evident audit trail. Self-serve from day one - no sales call.
                    </p>
                </div>
            </section>

            <!-- Three wedges -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:40px 0 60px;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-4">
                            <div class="mkt-card q-pa-lg" style="height:100%;border-top:4px solid #047857;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon name="payments" size="40px" style="color:#047857;" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.2rem;color:#0a0f1a;line-height:1.25;">Transparent pricing</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7" style="font-size:0.95rem;line-height:1.6;">
                                    Predictable per-signing overage as low as $0.25.
                                    Pay for what you send - no per-envelope tiers, no surprise upgrades.
                                </p>
                            </div>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="mkt-card q-pa-lg" style="height:100%;border-top:4px solid #B45309;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon name="admin_panel_settings" size="40px" style="color:#B45309;" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.2rem;color:#0a0f1a;line-height:1.25;">Identity &amp; compliance</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7" style="font-size:0.95rem;line-height:1.6;">
                                    Single sign-on, auto-provisioning, hash-chained audit trail,
                                    ID verification, knowledge-based quizzes - self-serve at $149/mo.
                                </p>
                            </div>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="mkt-card q-pa-lg" style="height:100%;border-top:4px solid #7C3AED;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon name="code" size="40px" style="color:#7C3AED;" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.2rem;color:#0a0f1a;line-height:1.25;">Developer-first API</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7" style="font-size:0.95rem;line-height:1.6;">
                                    Developer API, webhooks, and embedded signing at $49/mo.
                                    Self-serve. Pay only for what you send.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Soft curve divider: wedges → categories -->
            <svg class="mkt-divider-soft" viewBox="0 0 1440 56" preserveAspectRatio="none">
                <path d="M0,0 C320,48 720,56 1080,32 C1260,20 1360,24 1440,16 L1440,56 L0,56 Z" fill="#ffffff" />
            </svg>

            <!-- ============================================================
                 FEATURE BLOCKS - one ordered loop drives the whole body:
                 category sections, deep-dive spotlights, and the dark
                 signer-roles break, interleaved so the page never runs more
                 than two same-pattern sections in a row. Order + zebra
                 striping come from the featureBlocks computed; the category
                 markup lives here once and is reused via block.cat. The dark
                 roles section sits in the MIDDLE as a tonal palette-cleanser.
                 ============================================================ -->
            <template v-for="block in featureBlocks" :key="block.k">
                <!-- CATEGORY: 2-column section, fake-UI mockup dispatched by mockType -->
                <section v-if="block.type === 'category'"
                    v-intersection="revealCfg"
                    :class="[block.bg, block.reveal]" style="padding:60px 0;">
                    <div class="mkt-container">
                        <div class="row q-col-gutter-xl items-center" :class="block.reverse ? 'reverse' : ''">
                            <div class="col-12 col-md-6">
                                <q-item class="q-pa-none q-mb-md">
                                    <q-item-section avatar style="min-width:72px;">
                                        <div class="mkt-icon-circle" :class="block.cat.iconClass">
                                            <q-icon :name="block.cat.icon" />
                                        </div>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.6rem;color:#0a0f1a;line-height:1.2;">{{ block.cat.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-lg" style="font-size:1rem;line-height:1.7;">{{ block.cat.description }}</p>
                                <q-list dense class="mkt-feature-list">
                                    <q-item v-for="feature in block.cat.features" :key="feature" class="q-px-none">
                                        <q-item-section avatar top style="min-width:28px;padding-right:0;">
                                            <q-icon name="check_circle" color="positive" size="20px" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label style="font-size:0.95rem;color:#333;line-height:1.55;">{{ feature }}</q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                                <div v-if="block.cat.tier" class="q-mt-md">
                                    <q-badge :color="block.cat.tierColor" :label="block.cat.tier" />
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <!-- Per-category stylized fake-UI mockup. mockType
                                     dispatches to the right preview block below. -->
                                <div class="mkt-mockup-frame">
                                    <!-- Browser chrome - used by all variants except API
                                         (bare code block) and auth (illustrative artwork
                                         where chrome would feel out of place). -->
                                    <div v-if="block.cat.mockType !== 'api' && block.cat.mockType !== 'auth'" class="mkt-mockup-chrome">
                                        <span class="mkt-mockup-dot" style="background:#FF5F57;"></span>
                                        <span class="mkt-mockup-dot" style="background:#FFBD2E;"></span>
                                        <span class="mkt-mockup-dot" style="background:#28CA42;"></span>
                                        <span class="mkt-mockup-url">{{ block.cat.mockUrl }}</span>
                                    </div>

                                    <!-- SEND & SIGN -->
                                    <div v-if="block.cat.mockType === 'send'" class="mkt-mockup-body">
                                        <div class="mkt-mockup-row q-mb-md">
                                            <q-icon name="description" size="20px" color="primary" />
                                            <div class="text-weight-bold" style="font-size:0.9rem;">Service-Agreement.pdf</div>
                                            <q-space />
                                            <q-badge color="warning" label="Pending" />
                                        </div>
                                        <div class="mkt-mockup-line" style="width:90%;"></div>
                                        <div class="mkt-mockup-line" style="width:75%;"></div>
                                        <div class="mkt-mockup-line" style="width:82%;"></div>
                                        <div class="mkt-mockup-sigfield q-mt-md">
                                            <div class="mkt-mockup-sigcaption">Signature</div>
                                            <div class="mkt-mockup-sigink">Sarah Buchanan</div>
                                        </div>
                                        <div class="row items-center q-gutter-xs q-mt-sm">
                                            <q-icon name="check_circle" color="positive" size="14px" />
                                            <span style="font-size:0.7rem;color:#64748B;">Signed · 2 of 3 signers complete</span>
                                        </div>
                                    </div>

                                    <!-- TEMPLATES -->
                                    <div v-else-if="block.cat.mockType === 'templates'" class="mkt-mockup-body">
                                        <div v-for="t in mockTemplates" :key="t.name" class="mkt-mockup-template-row">
                                            <q-icon name="content_copy" size="18px" color="primary" />
                                            <div class="col">
                                                <div class="text-weight-bold" style="font-size:0.85rem;">{{ t.name }}</div>
                                                <div class="row q-gutter-xs q-mt-xs" style="flex-wrap:wrap;">
                                                    <q-chip v-for="r in t.roles" :key="r" dense :label="r" color="indigo-1" text-color="indigo-9" size="sm" />
                                                </div>
                                            </div>
                                            <q-icon name="arrow_forward" size="16px" color="grey-6" />
                                        </div>
                                    </div>

                                    <!-- IDENTITY & AUTH - illustrative artwork (not a
                                         fake-UI mockup) showing face + ID + record cards.
                                         WebP first, JPEG fallback for older clients. -->
                                    <picture v-else-if="block.cat.mockType === 'auth'" class="mkt-mockup-image">
                                        <source srcset="/img/pidv-1000.webp" type="image/webp" />
                                        <img src="/img/pidv-1000.jpg"
                                             alt="Identity verification - face, ID card, and record matching"
                                             loading="lazy" />
                                    </picture>

                                    <!-- COMPLIANCE & AUDIT -->
                                    <div v-else-if="block.cat.mockType === 'audit'" class="mkt-mockup-body">
                                        <div class="text-weight-bold q-mb-sm" style="font-size:0.85rem;">Audit trail · pkg_a1b2c3</div>
                                        <div v-for="(e, ei) in mockAuditEvents" :key="ei" class="mkt-mockup-audit-row">
                                            <div class="mkt-mockup-audit-time">{{ e.time }}</div>
                                            <div class="mkt-mockup-audit-dot" :style="`background:${e.color};`"></div>
                                            <div class="col">
                                                <div style="font-size:0.78rem;color:#0a0f1a;">{{ e.text }}</div>
                                                <div style="font-size:0.65rem;color:#94A3B8;font-family:monospace;">{{ e.ip }}</div>
                                            </div>
                                        </div>
                                        <div class="mkt-mockup-hashbar q-mt-sm">
                                            <q-icon name="vpn_key" size="12px" />
                                            <span>SHA-256 chain · 5d8f2c…</span>
                                        </div>
                                    </div>

                                    <!-- API & INTEGRATIONS - bare code block, no chrome -->
                                    <div v-else-if="block.cat.mockType === 'api'" class="mkt-code-block" style="font-size:0.78rem;line-height:1.5;border-radius:14px;">
<pre style="margin:0;white-space:pre;overflow-x:auto;color:#E2E8F0;"><span style="color:#64748B;"># Send for signature</span>
<span style="color:#93C5FD;">curl</span> <span style="color:#FCA5A5;">-X POST</span> <span style="color:#86EFAC;">https://api.esignlaunchpad.com/v1/packages</span> \
  <span style="color:#FCA5A5;">-H</span> <span style="color:#86EFAC;">"Authorization: Bearer $TOKEN"</span> \
  <span style="color:#FCA5A5;">-d</span> <span style="color:#86EFAC;">'{
    "name": "Service Agreement",
    "signers": [{"email": "jane@example.com"}],
    "send": true
  }'</span>

<span style="color:#64748B;"># Webhook fires when signed</span>
<span style="color:#C4B5FD;">event</span>: <span style="color:#86EFAC;">"package.completed"</span></pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- SPOTLIGHT: branded deep-dive band (props supplied by featureBlocks) -->
                <marketing-spotlight v-else-if="block.type === 'spotlight'" v-bind="block.spot" />

                <!-- ROLES: dark signer-roles gallery - the page's single tonal
                     break, deliberately placed mid-body rather than at the end. -->
                <section v-else-if="block.type === 'roles'" class="mkt-dark reveal-fade-in" v-intersection="revealCfg" style="padding:60px 0;">
                    <div class="mkt-container text-center">
                        <h2 class="text-white text-weight-bold q-mb-sm" style="font-size:1.8rem;">
                            Signer roles for <span class="text-mkt-indigo">every workflow</span>
                        </h2>
                        <p class="text-grey-5 q-mb-xl" style="font-size:1rem;">
                            More than just "signer" and "CC" - flexible roles handle complex approval chains and team workflows.
                        </p>
                        <div class="row q-col-gutter-md justify-center">
                            <div class="col-6 col-md-3" v-for="role in signerRoles" :key="role.name">
                                <div class="mkt-card--dark q-pa-md" style="border-radius:12px;text-align:center;">
                                    <q-icon :name="role.icon" size="32px" class="text-mkt-indigo q-mb-sm" />
                                    <div class="text-white text-weight-bold" style="font-size:0.9rem;">{{ role.name }}</div>
                                    <div class="text-grey-6" style="font-size:0.75rem;">{{ role.desc }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </template>

            <!-- CTA -->
            <section class="mkt-cta-gradient reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">See the plans behind the features</h2>
                    <div class="row justify-center q-gutter-md">
                        <q-btn color="white" text-color="primary" size="lg" label="View Pricing" href="/Home/Pricing"
                            no-caps unelevated style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                        <q-btn outline color="white" size="lg" label="Get Started Free" href="/Account/Register"
                            no-caps style="border-radius:10px;padding:10px 36px;" />
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <footer class="mkt-light" style="padding:40px 0 20px;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="row justify-between items-center">
                        <div class="row items-center">
                            <img src="/img/logo-nav.webp" alt="eSign Launchpad" width="82" height="22" style="height: 22px; width: auto;" />
                        </div>
                        <div class="row q-gutter-md">
                            <a href="/Home/Pricing" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Pricing</a>
                            <a href="/Home/SmallBusiness" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Small Business</a>
                            <a href="/Home/Security" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Security</a>
                            <a href="/news" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">News</a>
                            <a href="/privacy" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Privacy</a>
                            <a href="/terms" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Terms</a>
                        </div>
                        <div style="font-size:0.75rem;color:#64748B;">&copy; {{ currentYear }} eSign Launchpad</div>
                    </div>
                </div>
            </footer>
        </q-page-container>
    </q-layout>
</template>

<style scoped>
.reverse { flex-direction: row-reverse; }
</style>

<script>
import { logout } from '@services/authActions.js';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingSpotlight from '@components/marketing/MarketingSpotlight.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'FeaturesPage',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingSpotlight },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            currentYear: new Date().getFullYear(),

            categories: [
                {
                    icon: 'send', iconClass: 'mkt-icon-circle--green',
                    mockType: 'send', mockUrl: 'app.esignlaunchpad.com/sign',
                    title: 'Send & Sign',
                    description: 'Drag in a PDF or Word doc. Smart detection finds the fields. Send to one signer or many.',
                    features: [
                        'PDF & DOCX with auto-conversion',
                        '18 field types incl. signature, initials, date',
                        'Smart text-tag field detection',
                        'Sequential or simultaneous signing',
                        'Custom email per recipient'
                    ]
                },
                {
                    icon: 'content_copy', iconClass: 'mkt-icon-circle--blue',
                    mockType: 'templates', mockUrl: 'app.esignlaunchpad.com/templates',
                    title: 'Templates & Workflows',
                    description: 'Pre-place fields once. Reuse forever. Bulk-send to a list of recipients with role-based mapping.',
                    features: [
                        'Up to 1,000 templates on Business',
                        'Role-based (Buyer, Seller, etc.)',
                        'Pre-place fields once, reuse forever',
                        'Bulk send to many recipients',
                        'Clone + customize for variations'
                    ],
                    tier: 'Available on Starter+', tierColor: 'info'
                },
                {
                    icon: 'fingerprint', iconClass: 'mkt-icon-circle--gold',
                    mockType: 'auth', mockUrl: 'app.esignlaunchpad.com/verify',
                    title: 'Identity & Authentication',
                    description: 'Three escalating auth methods. Per-signer configurable. From cheap SMS to government-ID checks.',
                    features: [
                        'SMS one-time password',
                        'Knowledge-based identity quiz',
                        'Government ID verification',
                        'Configurable per signer',
                        'Logged in the audit trail'
                    ]
                },
                {
                    icon: 'verified_user', iconClass: 'mkt-icon-circle--green',
                    mockType: 'audit', mockUrl: 'app.esignlaunchpad.com/audit',
                    title: 'Compliance & Audit',
                    description: '30+ event types with IP + timestamp. SHA-256 hash chain detects tampering. Evidence ZIP on demand.',
                    features: [
                        '30+ auditable event types',
                        'IP + UTC timestamp on every event',
                        'SHA-256 hash-chained tamper detection',
                        'Certificate of completion',
                        'Evidence package ZIP export'
                    ]
                },
                {
                    icon: 'code', iconClass: 'mkt-icon-circle--blue',
                    mockType: 'api', mockUrl: 'api.esignlaunchpad.com',
                    title: 'API & Integrations',
                    description: 'Full developer API, real-time event notifications, and AI-agent integration. Build signing into anything.',
                    features: [
                        '22+ API endpoints',
                        '24 cryptographically-signed event types',
                        'Embedded iframe signing',
                        'AI-agent integration via MCP',
                        '100 API signings on Pro · 500 on Business'
                    ],
                    tier: 'API on Developer, Pro, and Business', tierColor: 'deep-purple'
                }
            ],

            // Mockup data - drives the per-category fake-UI previews on the right
            // side of each Feature Categories block. Pure presentation; no real
            // data, just enough realism to communicate the product surface.
            mockTemplates: [
                { name: 'Mutual NDA',         roles: ['Disclosing', 'Receiving'] },
                { name: 'Service Agreement',  roles: ['Client', 'Provider'] },
                { name: 'Lease Agreement',    roles: ['Tenant', 'Landlord'] }
            ],
            mockAuditEvents: [
                { time: '14:32', text: 'Sarah Chen signed the document', ip: '203.0.113.42 · encrypted', color: '#047857' },
                { time: '14:28', text: 'Sarah Chen viewed page 3',        ip: '203.0.113.42',           color: '#4F46E5' },
                { time: '14:26', text: 'Email delivered to recipient',    ip: 'mailgun.com',            color: '#0EA5E9' },
                { time: '14:25', text: 'Package sent for signature',      ip: '198.51.100.7 · admin',   color: '#64748B' }
            ],

            // Four signer roles - matches the product's closed SignerType set
            // (Signer / Approver / CertifiedDelivery / CarbonCopy). The legacy
            // "Editor" and "In-Person" roles were removed from the product and
            // must not appear in marketing copy.
            signerRoles: [
                { icon: 'draw', name: 'Signer', desc: 'Must sign all assigned fields' },
                { icon: 'thumb_up', name: 'Approver', desc: 'Reviews before signers' },
                { icon: 'visibility', name: 'Certified Delivery', desc: 'Must view & acknowledge' },
                { icon: 'mail', name: 'Carbon Copy', desc: 'Receives final documents' }
            ],

            // Deep-dive spotlight configs, consumed by featureBlocks via
            // v-bind. Keys are camelCase to match MarketingSpotlight props.
            spotlights: {
                signPay: {
                    slug: 'spot-sign-pay',
                    alt: 'Collect deposits, retainers, and invoices during the signing ceremony - funds settle straight to your bank',
                    eyebrowIcon: 'payments',
                    iconCircleClass: 'mkt-icon-circle--gold',
                    title: 'Sign and pay. In one flow.',
                    blurb: 'Collect retainers, deposits, and invoices during the signing ceremony itself. Funds settle straight to your Stripe-connected bank account - no separate payment links to chase, no reconciliation gymnastics.',
                    bullets: [
                        'Auth-and-capture or charge-on-submission per package',
                        'Multi-payer batching with automatic refund on failure',
                        'Free-tier signers prepay per-package via Stripe Elements',
                        'Audit trail records the payment event alongside the signature'
                    ],
                    ctaLabel: 'See plans with payments',
                    ctaHref: '/Home/Pricing',
                    flip: true
                },
                bulkSend: {
                    slug: 'spot-bulk-send',
                    alt: 'Bulk send one template to many recipients with per-row variables and live progress',
                    eyebrowIcon: 'dynamic_feed',
                    iconCircleClass: 'mkt-icon-circle--blue',
                    title: 'One template. A hundred signatures.',
                    blurb: 'Bulk-send a template to a CSV of recipients. Per-row variable substitution, parallel dispatch with rate limiting, live progress, and resumable retries when a row fails.',
                    bullets: [
                        'Upload a CSV, map columns to template fields',
                        'Append-only attempts model - retry failed rows safely',
                        'Live progress: succeeded / pending / failed counts',
                        'Per-batch audit and CSV-style results export'
                    ],
                    ctaLabel: 'See bulk send',
                    ctaHref: '/Home/Features',
                    flip: true
                },
                signingGroups: {
                    slug: 'spot-signing-groups',
                    alt: "Signing groups let any member of the group complete the signing on the group's behalf",
                    eyebrowIcon: 'groups',
                    iconCircleClass: 'mkt-icon-circle--green',
                    title: 'Anyone on the team can sign',
                    blurb: 'Signing groups let the first available group member close the signing on behalf of the whole group. Perfect for HR teams, AP queues, and shared inboxes where any signer can stand in.',
                    bullets: [
                        'Group membership managed by tenant admin',
                        'First-available member completes the signing',
                        'Audit trail names the individual who actually signed',
                        'Works with SCIM-provisioned group memberships'
                    ],
                    ctaLabel: 'Set up signing groups',
                    ctaHref: '/Home/Features'
                }
            }
        };
    },
    computed: {
        // Ordered page body. One loop renders categories, spotlights, and the
        // dark roles break so they interleave instead of running 8 identical
        // category sections in a row. Zebra striping (white/light + reverse)
        // is computed across CATEGORY blocks only, so the spotlights (wash)
        // and the dark roles section don't throw off the alternation.
        featureBlocks() {
            const cat = (mt) => this.categories.find((c) => c.mockType === mt);
            const raw = [
                { type: 'category', cat: cat('send') },
                { type: 'category', cat: cat('templates') },
                { type: 'spotlight', spot: this.spotlights.bulkSend },
                { type: 'category', cat: cat('auth') },
                { type: 'spotlight', spot: this.spotlights.signingGroups },
                { type: 'roles' },
                { type: 'spotlight', spot: this.spotlights.signPay },
                { type: 'category', cat: cat('audit') },
                { type: 'category', cat: cat('api') }
            ];
            let ci = 0;
            return raw.map((b) => {
                if (b.type === 'category') {
                    const even = ci % 2 === 0;
                    ci += 1;
                    return {
                        ...b,
                        k: 'cat-' + b.cat.mockType,
                        bg: even ? 'mkt-white' : 'mkt-light',
                        reveal: even ? 'reveal-fade-up' : 'reveal-scale',
                        reverse: !even
                    };
                }
                if (b.type === 'spotlight') return { ...b, k: 'spot-' + b.spot.slug };
                return { ...b, k: 'roles' };
            });
        }
    },
    methods: { logout }
};
</script>
